import { AnalyticsSdk, generateUUID, getTimeZone, getSessionID, fetchLocusData, buildSearchContext } from '../pdtUtils';
import {
  LOB_RAILS,
  EVENT_NAME_PAGE_RENDERED,
  EVENT_TYPE_PAGE,
  ORGANISATION_MMT,
  BU_GROUND_TRANSPORT,
  RIS_LTS_SCHEMA_ID,
  RIS_LTS_SCHEMA_NAME,
  PAGE_TYPE_FUNNEL,
  FUNNEL_STEP_DETAIL,
  FUNNEL_SOURCE_SEO,
  PAGE_NAME_HOME,
  SEO_PAGE_EVENT,
  XDM_PAGE_NAME
} from '../constants';

const stripNulls = (obj: any): any => {
  if (Array.isArray(obj)) return obj.map(stripNulls);
  if (obj && typeof obj === 'object') {
    const result: any = {};
    Object.keys(obj).forEach((key) => {
      if (obj[key] != null) {
        result[key] = stripNulls(obj[key]);
      }
    });
    return result;
  }
  return obj;
};

const mapTrainDetailsToBookingInfo = (trainDetails: any) => ({
  number: trainDetails?.Number ?? null,
  name: trainDetails?.Name ?? null,
  distance_covered: trainDetails?.DistanceCovered != null ? String(trainDetails.DistanceCovered) : null,
  start_date: trainDetails?.StartDate ?? null,
  start_day: trainDetails?.StartDay ?? null,
  start_day_difference: trainDetails?.StartDayDifference ?? null,
  total_journey: trainDetails?.TotalJourney ?? null
});

const mapStoppingStationToBookingInfo = (stoppingStation: any) => {
  if (!stoppingStation) return null;

  const dayDetails = stoppingStation?.DayDetails;
  const departureDetails = stoppingStation?.DepartureDetails;
  const arrivalDetails = stoppingStation?.ArrivalDetails;
  const stationInfo = stoppingStation?.Station;
  const platformVal = stationInfo?.expectedPlatformNumber;
  const expectedPlatformNumber =
    platformVal != null && platformVal !== '' && !Number.isNaN(Number(platformVal)) ? Number(platformVal) : null;

  return {
    halt_minutes: stoppingStation?.HaltMinutes != null ? String(stoppingStation.HaltMinutes) : null,
    journey_date: stoppingStation?.JourneyDate ?? null,
    distance: stoppingStation?.Distance ?? null,
    day_details: dayDetails
      ? {
          day_count: dayDetails?.dayCount ?? null,
          scheduled_day_count: dayDetails?.scheduledDayCount ?? null,
          day_difference: dayDetails?.dayDifference != null ? Number(dayDetails.dayDifference) : null
        }
      : null,
    departure_details: departureDetails
      ? {
          actual_departure_date: departureDetails?.actualDepartureDate ?? null,
          actual_departure_time: departureDetails?.actualDepartureTime ?? null,
          scheduled_departure_date: departureDetails?.scheduledDepartureDate ?? null,
          scheduled_departure_time: departureDetails?.scheduledDepartureTime ?? null
        }
      : null,
    arrival_details: arrivalDetails
      ? {
          actual_arrival_date: arrivalDetails?.actualArrivalDate ?? null,
          actual_arrival_time: arrivalDetails?.actualArrivalTime ?? null,
          scheduled_arrival_date: arrivalDetails?.scheduledArrivalDate ?? null,
          scheduled_arrival_time: arrivalDetails?.scheduledArrivalTime ?? null
        }
      : null,
    station: stationInfo
      ? {
          name: stationInfo?.name ?? null,
          code: stationInfo?.code ?? null,
          expected_platform_number: expectedPlatformNumber
        }
      : null
  };
};

const mapCurrentStationToBookingInfo = (currentStationDetail: any) =>
  mapStoppingStationToBookingInfo(currentStationDetail?.StoppingStation ?? currentStationDetail ?? null);

const mapTopStationToBookingInfo = (topStation: any) => mapStoppingStationToBookingInfo(topStation ?? null);

const mapContentDetailsItems = (stations: any[]) => {
  const contentDetails: any[] = [];

  stations?.forEach((station: any) => {
    if (!station) return;
    const stationInfo = station?.Station;
    if (!stationInfo) return;

    const platformVal = stationInfo?.expectedPlatformNumber;
    const expectedPlatformNumber =
      platformVal != null && platformVal !== '' && !Number.isNaN(Number(platformVal)) ? Number(platformVal) : null;

    const arrivalDetails = station?.ArrivalDetails;
    const departureDetails = station?.DepartureDetails;
    const dayDetails = station?.DayDetails;

    contentDetails.push({
      station: {
        name: stationInfo?.name ?? null,
        code: stationInfo?.code ?? null,
        expected_platform_number: expectedPlatformNumber
      },
      journey_date: station?.JourneyDate ?? null,
      halt_minutes: station?.HaltMinutes != null ? String(station.HaltMinutes) : null,
      arrival_details: arrivalDetails
        ? {
            actual_arrival_time: arrivalDetails?.actualArrivalTime ?? null,
            scheduled_arrival_time: arrivalDetails?.scheduledArrivalTime ?? null,
            actual_arrival_date: arrivalDetails?.actualArrivalDate ?? null,
            scheduled_arrival_date: arrivalDetails?.scheduledArrivalDate ?? null
          }
        : null,
      departure_details: departureDetails
        ? {
            actual_departure_time: departureDetails?.actualDepartureTime ?? null,
            scheduled_departure_time: departureDetails?.scheduledDepartureTime ?? null,
            actual_departure_date: departureDetails?.actualDepartureDate ?? null,
            scheduled_departure_date: departureDetails?.scheduledDepartureDate ?? null
          }
        : null,
      day_details: dayDetails
        ? {
            day_count: dayDetails?.dayCount ?? null,
            scheduled_day_count: dayDetails?.scheduledDayCount ?? null,
            day_difference: dayDetails?.dayDifference != null ? Number(dayDetails.dayDifference) : null
          }
        : null
    });
  });

  return contentDetails;
};

const mapProductListItems = (stations: any[]) => {
  const productList: any[] = [];

  stations?.forEach((station: any) => {
    if (!station) return;

    const aggregatorsAvailable: Array<{ name: string | null; code: string | null }> = [];
    if (station?.MealsAvailable === true) aggregatorsAvailable.push({ name: 'RelFood', code: 'relfood' });
    if (station?.ZomatoMealsAvailable === true) aggregatorsAvailable.push({ name: 'Zomato', code: 'zomato' });
    if (aggregatorsAvailable.length === 0) return;

    productList.push({
      station_name: station?.Station?.name ?? null,
      station_code: station?.Station?.code ?? null,
      aggregators_available: aggregatorsAvailable
    });
  });

  return productList;
};

const mapOtherDetails = (otherDetails: any) => {
  if (!otherDetails) return null;
  const { DistanceDetail, TimeDetail } = otherDetails;
  const hasValue = (v: any) => v != null && v !== '';
  if (!hasValue(DistanceDetail) && !hasValue(TimeDetail)) return null;
  return { distance_detail: DistanceDetail ?? null, time_detail: TimeDetail ?? null };
};

export const logLtsPageLoadEvent = async (liveStatusApiResponse: any) => {
  try {
    if (typeof window === 'undefined') return;
    if (!liveStatusApiResponse) return;

    if (!AnalyticsSdk) return;
    const isDev = process.env.NODE_ENV !== 'production';
    const sessionId = getSessionID();
    const journeyId = generateUUID();

    const trainDetails = liveStatusApiResponse?.TrainDetails;
    const stations = liveStatusApiResponse?.Stations || [];
    const metaDetails = liveStatusApiResponse?.MetaDetails;
    const disclaimer = liveStatusApiResponse?.Disclaimer ?? null;
    const provider = liveStatusApiResponse?.Provider ?? null;

    const originStation = stations[0]?.Station;
    const destinationStation = stations[stations.length - 1]?.Station;
    const fromCode = originStation?.code || 'NA';
    const fromName = originStation?.name || 'NA';
    const toCode = destinationStation?.code || 'NA';
    const toName = destinationStation?.name || 'NA';

    const locusData = await fetchLocusData(
      fromCode !== 'NA' ? fromCode : undefined,
      toCode !== 'NA' ? toCode : undefined
    );

    const searchContext = buildSearchContext({ fromCode, fromName, toCode, toName, locusData });

    const mappedTrainDetails = mapTrainDetailsToBookingInfo(trainDetails);
    const mappedCurrentStation = mapCurrentStationToBookingInfo(metaDetails?.CurrentStation);
    const mappedTopStation = mapTopStationToBookingInfo(metaDetails?.TopStation);
    const mappedBottomStation = mapTopStationToBookingInfo(metaDetails?.BottomStation);
    const contentDetailsItems = mapContentDetailsItems(stations);
    const productListItems = mapProductListItems(stations);
    const mappedOtherDetails = mapOtherDetails(metaDetails?.OtherDetails);

    const components: any = {
      content_details: contentDetailsItems.length > 0 ? contentDetailsItems : [{}]
    };
    if (productListItems.length > 0) {
      components.product_list = productListItems;
    }

    const payload = stripNulls({
      page_context: {
        lob: LOB_RAILS,
        page_type: PAGE_TYPE_FUNNEL,
        page_name: XDM_PAGE_NAME.LTS_DETAILS,
        prev_page_name: PAGE_NAME_HOME,
        funnel_step: FUNNEL_STEP_DETAIL,
        lob_category: LOB_RAILS
      },
      event_tracking_context: {
        env: isDev ? 'dev' : 'prod',
        timezone: getTimeZone(),
        session_id: sessionId,
        funnel_source: FUNNEL_SOURCE_SEO,
        bu: BU_GROUND_TRANSPORT,
        organisation: ORGANISATION_MMT,
        journey_id: journeyId,
        template_id: RIS_LTS_SCHEMA_ID,
        topic_name: RIS_LTS_SCHEMA_NAME
      },
      search_context: searchContext,
      event_detail: {
        event_name: EVENT_NAME_PAGE_RENDERED,
        event_type: EVENT_TYPE_PAGE,
        event_id: generateUUID(),
        event_timestamp: Date.now(),
        event_value: SEO_PAGE_EVENT.LTS_DETAILS,
        booking_info: {
          train_details: mappedTrainDetails,
          current_station: mappedCurrentStation,
          top_station: mappedTopStation,
          bottom_station: mappedBottomStation,
          disclaimer,
          provider,
          other_details: mappedOtherDetails
        },
        components
      },
      experiment_details: {
        valid_exp_list: [] as string[],
        variant_keys: '',
        honoured_exp_list: [] as string[]
      }
    });

    const xdmPayload = stripNulls({
      ...payload,
      event_detail: {
        ...payload.event_detail,
        components: [components]
      }
    });

    const extraOptions = { lob: LOB_RAILS, destinationUrl: window?.location?.href || '' };

    AnalyticsSdk?.pushToPDT?.(payload, extraOptions);
    AnalyticsSdk?.pushToAdobeDataLayer?.(xdmPayload, extraOptions);
  } catch (e) {
    console.error('Analytics: LTS page load event failed', e);
  }
};
